"use client";

import { useEffect, useState, useCallback, useRef } from "react";
import { shouldForceReload } from "@/lib/version";

interface VersionCheckConfig {
  /** Interval in milliseconds to check for version updates (default: 5 minutes) */
  checkInterval?: number;
  /** Whether to pause checks when tab is not visible (default: true) */
  pauseWhenInactive?: boolean;
}

interface UseVersionCheckReturn {
  /** Whether a version mismatch requiring reload has been detected */
  shouldReload: boolean;
  /** The current client version */
  clientVersion: string;
  /** The server version (if fetched) */
  serverVersion: string | null;
  /** Trigger a manual version check */
  checkVersion: () => Promise<void>;
  /** Dismiss the version mismatch modal for the current version pair */
  dismissVersion: () => void;
}

const DEFAULT_CHECK_INTERVAL = 30 * 1000;
const STORAGE_KEY = "version_check_dismissed";

export const useVersionCheck = (
  config: VersionCheckConfig = {},
): UseVersionCheckReturn => {
  const { checkInterval = DEFAULT_CHECK_INTERVAL, pauseWhenInactive = true } =
    config;

  const [shouldReload, setShouldReload] = useState(false);
  const [serverVersion, setServerVersion] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isCheckingRef = useRef(false);

  const clientVersion =
    process.env.NEXT_PUBLIC_APP_VERSION || "0.0.0";

  const checkVersion = useCallback(async () => {
    // Prevent concurrent checks
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;

    try {
      const response = await fetch("/api/version", {
        cache: "no-store",
      });

      if (!response.ok) {
        console.error("Failed to fetch version:", response.statusText);
        return;
      }

      const data = await response.json();
      const fetchedServerVersion = data.version;

      setServerVersion(fetchedServerVersion);

      // Check if we need to force reload
      const needsReload = shouldForceReload(
        clientVersion,
        fetchedServerVersion,
      );

      if (needsReload) {
        // Check localStorage to prevent showing modal again if already dismissed for this version
        const dismissedVersion = localStorage.getItem(STORAGE_KEY);

        // Only show modal if it hasn't been dismissed for this exact version pairing
        if (dismissedVersion !== `${clientVersion}-${fetchedServerVersion}`) {
          setShouldReload(true);
        }
      }
    } catch (error) {
      console.error("Error checking version:", error);
    } finally {
      isCheckingRef.current = false;
    }
  }, [clientVersion]);

  // Handle visibility change to pause/resume checks
  useEffect(() => {
    if (!pauseWhenInactive) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, clear interval
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      } else {
        // Tab is visible, check immediately and restart interval
        checkVersion();
        intervalRef.current = setInterval(checkVersion, checkInterval);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [checkVersion, checkInterval, pauseWhenInactive]);

  // Set up periodic version checking
  useEffect(() => {
    // Only check if we have a valid client version
    if (!clientVersion || clientVersion === "0.0.0") {
      console.warn(
        "Version checking disabled: NEXT_PUBLIC_APP_VERSION not set",
      );
      return;
    }

    // Initial check
    checkVersion();

    // Set up interval (if tab is visible or pauseWhenInactive is false)
    if (!pauseWhenInactive || !document.hidden) {
      intervalRef.current = setInterval(checkVersion, checkInterval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [checkVersion, checkInterval, clientVersion, pauseWhenInactive]);

  const dismissVersion = useCallback(() => {
    if (serverVersion) {
      localStorage.setItem(STORAGE_KEY, `${clientVersion}-${serverVersion}`);
      setShouldReload(false);
    }
  }, [clientVersion, serverVersion]);

  return {
    shouldReload,
    clientVersion,
    serverVersion,
    checkVersion,
    dismissVersion,
  };
};

